/** @format */

import {
	ChangeUrlQueryValues,
	GetQueryValues,
} from "_shared/helpers/routerHelper"
import {
	ChildContainer,
	Content,
	DashboardContainer,
	Header,
	Line,
	ParentContainer,
	TabsContainer,
	Title,
} from "./Dashboard.styled"
import { ConsumptionSummary, DashboardStats } from "_components/molecules"
import {
	Contract,
	DashboardProps,
	getProductTypeEnum,
	getProductTypeEnumFromId,
} from "./Dashboard.types"
import Switch, { SwitchChoice } from "@ui-library/core/Switch"
import Tabs, { Choice } from "@ui-library/core/Tabs"
import { GetDistributionDetailsResponse } from "_clients/BFFClient"
import { useEffect, useState } from "react"
import { BannerBuyMore } from "_components/molecules/BannerBuyMore/BannerBuyMore"
import { BuyMoreBoostsDistributionRequest } from "../../molecules/BuyMorePopin/BuyMorePopin.types"
import { ProductTypeKeysMapper } from "_components/molecules/ConsumptionSummary/ConsumptionSummary.types"
import querystring from "querystring"
import { useAvailableBoostStore } from "_stores/availableBoost/availableBoost"
import useNetworkMarketsData from "_shared/hooks/useNetworkMarketsData"
import { ContractsAutocomplete } from "_components/atoms/ContractsAutocomplete/ContractsAutocomplete"
import { SearchParamsType } from "../CreditDispatchDashboard/CreditDispatchDashboard.types"
import { getParmsForQuery } from "../CreditDispatchDashboard/CreditDispatchDashboard"
import { NoContractsFound } from "_components/atoms/NoContractsFound/NoContractsFound"
import { useRouter } from "next/router";

/** @format */
type InnerProps = DashboardProps

const Dashboard = (props: InnerProps) => {
	const { isHomePage = true, title } = props;
	const router = useRouter();
	const [searchParams, setSearchParams] = useState<SearchParamsType>(getParmsForQuery(router.asPath))
	const [contracts, setContracts] = useState<Contract[]>([]);
	const { networkMarkets, isLoading } = useNetworkMarketsData(props.networkId, props.contractId);
    const [selectedContract, setSelectedContrat] = useState<number>();
	const [marketSelected, setMarketSelected] = useState<string>()

	const [productChoices, setProductChoices] = useState<SwitchChoice<string>[]>(
		[]
	)
	const [productSelected, setProductSelected] = useState<string>()

	const [consumptionInfo, setConsumptionInfo] =
		useState<GetDistributionDetailsResponse>(null)

	const [buyMoreBoostsDistribution, setBuyMoreBoostsDistribution] =
		useState<BuyMoreBoostsDistributionRequest>()

	useEffect(() => {
		if(!networkMarkets || networkMarkets.length  == 0) {
			setProductSelected(null);
			setProductChoices([]);
			setConsumptionInfo(null);
		}
	}, [networkMarkets])

	const getMarketId = () => {
		return GetQueryValues(router.asPath).marketId
	}

	const getProductId = () => {
		return GetQueryValues(router.asPath).productId
	}

	const getNetworkId = () => {
		return GetQueryValues(router.asPath).networkId
	}

	const getContractId = () => {
		return GetQueryValues(router.asPath).contractId
	}

	const onMarketChange = (choice: Choice) => {
		const market = networkMarkets.find(m => m.id.toString() === choice.id)
		const product = market?.products.find(
			p => p.id.toString() === productSelected
		)
		const productId = product ? productSelected : market?.products[0].id.toString();

		const queryParams = {
			marketId: choice.id,
			productId: productId,
			pageNumber: undefined,
			searchTerm: undefined,
			contractId: contracts?.find(c => c.marketId == Number(choice.id) && c.productId == Number(productId))?.contractId,
		}

		const url = ChangeUrlQueryValues(router.asPath, queryParams)
		router.push(url)
	}

	const onProductChange = (value: string) => {
		const url = ChangeUrlQueryValues(router.asPath, 
			{ 
				productId: value, 
				pageNumber: undefined, 
				searchTerm: undefined,
				contractId: contracts?.find(c => c.marketId == getMarketId() && c.productId == Number(value))?.contractId,
			})
		router.push(url)
	}

	const initializeMarketAndProductQueryParams = (
		marketValue?: string,
		productValue?: string,
		contractId?: number
	) => {

		var url = ChangeUrlQueryValues(router.asPath, {
			productId: productValue,
			marketId: marketValue,
			contractId: contractId,
		})
		router.push(url)
	}

	const initBuyMoreBoostsDistribution = (
		distributionDetails: GetDistributionDetailsResponse,
		marketId: number
	) => {
		const market = networkMarkets.find(m => m.id == marketId)
		const buyMoreBoostsDistribution: BuyMoreBoostsDistributionRequest = {
			networkId: props.networkId,
			marketId: marketId,
			marketName: market?.label,
			productType: getProductTypeEnumFromId(productSelected),
			consumedBoosts: distributionDetails?.consumed,
			notConsumedBoosts: distributionDetails?.notUsed,
			contractId: props.contractId,
		} 
		setBuyMoreBoostsDistribution(buyMoreBoostsDistribution)
	}

	const getFirstProductOfMarketDetails = (networkId, networkMarkets, contractId) => {
		if (networkId) {
		  return { 
			total: networkMarkets[0].products[0].totalAmountOfCredits,
			assigned: networkMarkets[0].products[0].totalAttributedOfCredits,
			consumed: networkMarkets[0].products[0].totalAmountOfUsedCredits, 
			available: networkMarkets[0].products[0].totalAvailableBoosts,
			assignedNotUsed: networkMarkets[0].products[0].totalAssignedNotUsedBoosts,
			notUsed: networkMarkets[0].products[0].totalNotUsedBoosts,
			consumption: networkMarkets[0].products[0].consumption,
		  };
		}
		return networkMarkets[0].products[0].contracts.find(c => c.contractId == Number(contractId));
	  }
	  
	  const getSelectedProductMarketDetails = (networkId, market, productId, contractId) => {
		const product = market?.products?.find(product => product.id == productId);
		if (networkId) {
		  return {
			total: product?.totalAmountOfCredits,
			assigned: product?.totalAttributedOfCredits,
			consumed: product?.totalAmountOfUsedCredits, 
			available: product?.totalAvailableBoosts,
			assignedNotUsed: product?.totalAssignedNotUsedBoosts,
			notUsed: product?.totalNotUsedBoosts,
			consumption: product?.consumption,
		  };
		}
		return product?.contracts.find(c => c.contractId == Number(contractId));
	  }

	useEffect(() => {
		const productId = getProductId()
		const marketId = getMarketId()
		if (!productId && !marketId && networkMarkets.length > 0 && !isLoading) {
			initializeMarketAndProductQueryParams(
				networkMarkets[0].id.toString(),
				networkMarkets[0].products[0].id.toString(),
				networkMarkets[0].products[0].contracts?.[0].contractId
			)
		}
	}, [networkMarkets, isLoading])

	useEffect(() => {
		if (networkMarkets && networkMarkets.length > 0) {
			const marketId = getMarketId()
			if (marketId) {
				setMarketSelected(marketId)
				const market = networkMarkets.find(m => m.id == marketId)
				setProductChoices(
					market?.products.map(product => ({
						value: product.id.toString(),
						label: ProductTypeKeysMapper[getProductTypeEnum(product.label)],
						key: product.id.toString(),
					}))
				)
				const productId = getProductId();
				setProductSelected(productId ?? networkMarkets[0].products[0].id.toString());

				const networkId = getNetworkId();
				const distributionDetails = productId 
					? getSelectedProductMarketDetails(networkId, market, productId, props.contractId) 
					: getFirstProductOfMarketDetails(networkId, networkMarkets, props.contractId);

				setConsumptionInfo(distributionDetails);
  				initBuyMoreBoostsDistribution(distributionDetails, marketId);
			}

			if(isInde && networkMarkets[0].products.some(p => p.contracts)){
				const contracts = networkMarkets.flatMap(market => 
					market.products.flatMap(product => 
						product.contracts?.flatMap(contract => ({ ...contract, productId: product.id, marketId: market.id, marketName: market.label }))
					)
				);
				setContracts(contracts);
				setSelectedContrat(props.contractId ?? contracts?.find(c => c?.marketId.toString() == marketSelected && c.productId == getProductId())?.contractId);
			}
		}
	}, [router.asPath, networkMarkets, productSelected]);

	const productType = networkMarkets
		?.find(market => market.id.toString() == marketSelected)
		?.products?.find(product => product.id.toString() == productSelected)?.label

	const visibilityThreshold = 50
	const { findAvailableBoost, availableBoosts } = useAvailableBoostStore()
	const [availableCount, setAvailableCount] = useState<number>(
		consumptionInfo?.available
	)
	const [assignedNotUsed, setAssignedNotUsed] = useState<number>(
		consumptionInfo?.assignedNotUsed
	)

	useEffect(() => {
		const newAvailableBoost = findAvailableBoost(
			getNetworkId(),
			getContractId(),
			Number(marketSelected),
			getProductTypeEnumFromId(productSelected)
		)

		setAvailableCount(
			newAvailableBoost?.newTotalAvailableBoosts ?? consumptionInfo?.available
		)
		setAssignedNotUsed(
			newAvailableBoost?.newDistributedNotUsedBoosts ??
				consumptionInfo?.assignedNotUsed
		)
	}, [consumptionInfo, availableBoosts]);

	const hasRetrievedEmptyResults = !isLoading && !consumptionInfo;
	const isInde = props.networkId ? false : true;
	const hasMarketAndProduct = getMarketId() && getProductId();

	useEffect(() => {
		const searchParams = getParmsForQuery(router.asPath)
		setSearchParams(searchParams)
	}, [router.asPath])


	return (!hasRetrievedEmptyResults || isInde ?
		<DashboardContainer>
			<ParentContainer>
				<Line>
					<Title isLoading={isLoading}>{title ?? "Ma consommation et répartition réseau"}</Title>
					{isInde && 
					<ContractsAutocomplete
						isLoading={isLoading}
						contracts={contracts}
						selectedContract={selectedContract}
						onSelect={setSelectedContrat}
					/>}
				</Line>
				{ (!isInde || isInde && (isLoading || hasMarketAndProduct)) &&
				<>
					<TabsContainer isLoading={isLoading}>
						{networkMarkets && networkMarkets.length> 0 && <Tabs
							choices={networkMarkets?.map(market => ({
								id: market?.id.toString(),
								label: market.label,
							}))}
							selected={marketSelected}
							onChange={onMarketChange}
							kind="secondary"
							type="normal"
						/>}
					</TabsContainer>
					<ChildContainer>
						<Header isLoading={isLoading}>
							<div>
								<Switch
									choices={productChoices}
									value={productSelected}
									onChange={onProductChange}
									testID="switch-products"
								/>
							</div>
						</Header>
						<Content>
							<DashboardStats
								isLoading={isLoading}
								availableCount={availableCount}
								notUsedCount={assignedNotUsed}
								usedCount={consumptionInfo?.consumed}
							/>
							<ConsumptionSummary
								isLoading={isLoading}
								searchParams={searchParams}
								productType={[getProductTypeEnum(productType)]}
								consumptionInfo={consumptionInfo}
								showDispatchButton={isHomePage}
								dispatchUrl={`creditsDispatch?${querystring.stringify(
									router.query
								)}`}
								buyMoreBoostsDistribution={buyMoreBoostsDistribution}
							/>
						</Content>
					</ChildContainer>
					{isHomePage && consumptionInfo?.consumption > visibilityThreshold && (
						<BannerBuyMore
							buyMoreBoostsDistribution={buyMoreBoostsDistribution}
						/>
					)}
				</>}
			</ParentContainer>
		</DashboardContainer> : <NoContractsFound/>
	)
}

export default Dashboard
